import { Avatar, Button, Divider, Form, FormInstance, Input, InputNumber, Radio, RadioChangeEvent, Row, Select, Space, Upload } from 'antd'

import { FileImageOutlined, UploadOutlined } from '@ant-design/icons'
import { TextAreaWrapper } from 'atom/input'
import configProvider from 'config'
import { useListingBasicTrans } from 'hooks/translation/useListingBasicTrans'
import { useNotifications } from 'hooks/useNotification'
import { SwtchError } from 'models/error'
import { Image } from 'models/image'
import { ListingFull } from 'models/listing'
import { useEffect, useState } from 'react'
import { GetImages, UploadChargerModelTemplateImage } from 'services/data-provider/image'
import { theme } from 'theme'
import { useListingChargerTranslation } from '../../hooks/translation/useListingChargerTrans'
import { chargingLevelOption } from '../../helpers/listing-select-option'

interface props {
  form: FormInstance<any>
  setError?: (error: SwtchError | undefined) => void
  listing?: ListingFull
}

export const ListingChargerV2: React.FC<props> = ({ form, setError, listing }) => {
  const { customNotifText, customNotifPlaceholderText, chargerText, chargerOutputText, maximumRatedOutputText, chargerTypeText } = useListingChargerTranslation()

  const [file, setFile] = useState<any>(null)
  const [imageUploading, setImageUploading] = useState(false)
  const [imageSource, setImageSource] = useState<string | null>(null)
  const [chargerType, setChargerType] = useState(form.getFieldValue('chargerType') || chargingLevelOption[0]?.value)

  const [images, setImages] = useState<Image[]>([])
  const [loading, setLoading] = useState(false)
  const { openSuccessNotification, openErrorNotification } = useNotifications()
  const {
    imageNotPNGText,
    imageSmallerThanMBText,
    uploadText,
    selectExistingImageText,
    noExistingImageText,
    uploadFailedText,
    uploadSuccessText,
    uploadPleaseSelectFileText,
    imageSizeText,
  } = useListingBasicTrans()

  const s3load = (objectKey?: string, fileName?: string) => {
    if (!objectKey || !fileName) {
      return ''
    }
    return `https://s3.amazonaws.com/${configProvider.config.s3Bucket}/${objectKey}/original/${fileName}`
  }

  const [currentImage, setCurrentImage] = useState<string>(s3load(listing?.imageObjectKey, listing?.imageFileName))

  const isSwtch = configProvider.config.network.toLowerCase() === 'swtch'
  useEffect(() => {
    if (isSwtch) {
      fetchImages()
    }
  }, [])

  const checkFileFormat = (file: any) => {
    const isPNG = file.type === 'image/png'
    if (!isPNG) {
      setFile(null)
      openErrorNotification(imageNotPNGText.replace('{name}', file.name))
    }
    return isPNG || Upload.LIST_IGNORE
  }

  const checkFileSize = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      setFile(null)
      openErrorNotification(imageSmallerThanMBText.replace('{size}', '2'))
      return setFile(null)
    }
  }

  const handleFileChange = (e: any) => {
    if (e === undefined || e === null || e === '' || e.target.files.length === 0) {
      setFile(null)
      setImageSource(null)
      setCurrentImage(s3load(undefined, undefined))
      return
    }
    const selectedFile = e.target.files[0]
    if (!selectedFile) {
      setFile(null)
      setCurrentImage(s3load(undefined, undefined))
      return
    }
    if (selectedFile) {
      checkFileFormat(selectedFile)
      checkFileSize(selectedFile)
      setFile(selectedFile)
    } else {
      setFile(null)
      setCurrentImage(s3load(undefined, undefined))
    }
  }

  const handleExistingImageHelp = () => {
    if (images.length === 0) {
      return noExistingImageText
    }
    return null
  }

  const handleImageSubmit = async () => {
    if (!file) {
      openErrorNotification(uploadPleaseSelectFileText)
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    try {
      setImageUploading(true)
      const response = await UploadChargerModelTemplateImage(formData)
      if (response.status === 'success') {
        openSuccessNotification(uploadSuccessText)
        // Set the form field value when file is uploaded successfully
        form.setFieldsValue({ imageFileName: response.data.image_file_name })
        form.setFieldsValue({ imageObjectKey: extractPathFromS3Url(response.data.image_path) })
        setCurrentImage(s3load(extractPathFromS3Url(response.data.image_path), response.data.image_file_name))
      } else {
        openErrorNotification(uploadFailedText)
      }
    } catch (error) {
      openErrorNotification(uploadFailedText)
      console.error('Error:', error)
    }
    setImageUploading(false)
  }

  // Extract just the path part from the S3 URL (e.g., "test/1745250518059")
  const extractPathFromS3Url = (url: string) => {
    try {
      const urlObj = new URL(url)
      // Remove the leading slash and return the pathname
      return urlObj.pathname.substring(1)
    } catch (error) {
      // If URL parsing fails, try to extract using string manipulation
      const match = url.match(/\.com\/(.+)/)
      return match ? match[1] : url
    }
  }

  const handleExistingImageSelect = (value?: string) => {
    setImageSource('existing')
    if (!value) {
      setImageSource(null)
      form.setFieldsValue({ imageFileName: undefined })
      form.setFieldsValue({ imageObjectKey: undefined })
      setCurrentImage(s3load(undefined, undefined))
      return
    }
    const selectedImage = images.find((image) => image.imageFileName === value)
    const selectedObjectKey = selectedImage?.imagePath

    const imagePath = selectedObjectKey ? extractPathFromS3Url(selectedObjectKey) : undefined
    // Set the form field values when an existing image is selected
    form.setFieldsValue({ imageFileName: value })
    form.setFieldsValue({ imageObjectKey: imagePath })
    setCurrentImage(s3load(imagePath, value))
  }

  const handleFileSelect = (e: any) => {
    setImageSource('upload')
    handleFileChange(e)
    // Clear the form field when switching to upload mode
    form.setFieldsValue({ imageFileName: undefined })
    form.setFieldsValue({ imageObjectKey: undefined })
  }

  const fetchImages = () => {
    setError?.(undefined)
    setLoading(true)
    GetImages()
      .then((response) => setImages(response.data))
      .catch((err) => setError?.(err))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (listing?.imageObjectKey && listing?.imageFileName) {
      setImageSource('existing')
    }
    // Set chargerType from form if it exists (form may have initial values)
    const formChargerType = form.getFieldValue('chargerType')
    if (formChargerType) {
      setChargerType(formChargerType)
    } else {
      // Check if listing has charger data (API may return charger object even though it's not in TypeScript interface)
      const listingCharger = (listing as any)?.charger
      if (listingCharger?.chargingType) {
        // Use chargingType directly from API (now matches form value 'DC')
        setChargerType(listingCharger.chargingType)
        form.setFieldsValue({ chargerType: listingCharger.chargingType })
      }
    }
  }, [listing?.imageObjectKey, listing?.imageFileName, listing, form])

  return (
    <>
      <Divider>{chargerText}</Divider>

      <Form.Item label={chargerOutputText} name="output" rules={[{ required: true }]} extra={
        listing?.chargerMaxWatt && <span style={{ fontSize: '12px', color: theme.colors.darkGray }}>{maximumRatedOutputText}: {listing?.chargerMaxWatt/1000} kW</span>
      }>
        <InputNumber style={{ width: '100%' }} precision={1} step="0.1" min={0} max={listing?.chargerMaxWatt ? listing?.chargerMaxWatt/1000 : 50} />
      </Form.Item>
      <Form.Item label={chargerTypeText} name="chargerType" rules={[{ required: true }]}>
        <Radio.Group 
          onChange={(e: RadioChangeEvent) => {
            const value = e.target.value
            setChargerType(value)
            form.setFieldsValue({ chargerType: value })
          }} 
          buttonStyle="solid"
        >
          <Space wrap>
            {chargingLevelOption.map((option) => (
              <Radio.Button key={option.value} value={option.value} className="radio-button">
                {option.label}
              </Radio.Button>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
      <TextAreaWrapper>
        <Form.Item label={customNotifText} name="customNotification" rules={[{ required: false }]}>
          <Input.TextArea maxLength={100} placeholder={customNotifPlaceholderText} style={{ height: theme.space[5] }} />
        </Form.Item>
      </TextAreaWrapper>
      {isSwtch && (
        <Row style={{ marginLeft: '25%', gap: '10px' }}>
          <Form.Item
            name="imageFileName"
            wrapperCol={{ span: 24 }}
            validateStatus={images.length === 0 ? 'error' : imageSource === 'upload' ? 'warning' : 'success'}
            help={handleExistingImageHelp()}
          >
            <Select
              loading={loading}
              disabled={loading || images.length === 0 || imageSource === 'upload'}
              placement="topLeft"
              placeholder={selectExistingImageText}
              dropdownMatchSelectWidth={false}
              allowClear
              onChange={handleExistingImageSelect}
            >
              {images.map((image, index) => (
                <Select.Option key={`${image.imageFileName}-${index}`} value={image.imageFileName}>
                  <Avatar src={image.thumbUrl} gap={4} size="small" icon={<FileImageOutlined />} />{' '}
                  {image.imageFileName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <span style={{ display: 'flex', alignItems: 'normal', marginTop: '12px' }}>OR</span>
          <div>
            <Input
              type="file"
              onChange={handleFileSelect}
              allowClear
              disabled={imageSource === 'existing'}
              style={{
                borderRadius: '48px',
              }}
              className="custom-input"
            />
            <div>
              <span style={{ fontSize: '12px', color: theme.colors.darkGray }}>{imageSizeText}</span>
            </div>
          </div>
          <Button icon={<UploadOutlined />} disabled={!file} loading={imageUploading} onClick={handleImageSubmit}>
            {uploadText}
          </Button>
        </Row>
      )}
      {isSwtch && currentImage && (
        <img
          alt="charger-model"
          style={{
            marginLeft: '40%',
            width: '20%',
            objectFit: 'contain',
          }}
          src={currentImage}
        />
      )}
      <Form.Item name="tenantId" hidden>
        <span></span>
      </Form.Item>
    </>
  )
}
